/**
 * Scout Studio — Odpowiedzi (Faza 2, prawdziwe AI).
 * Wklejasz wiadomość/komentarz → Scout robi triage (typ, priorytet, czy lead)
 * i przygotowuje draft odpowiedzi w tonie klienta. Ty akceptujesz i kopiujesz.
 */
import { useState } from 'react';
import { API } from '@/utils/api';
import {
  MessageSquare, Loader2, Copy, Check, Sparkles, Flame, User, Zap,
} from 'lucide-react';

const chip = 'inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold';
const panel = 'rounded-2xl border border-[var(--sc-border)] bg-[var(--sc-surface)]';

const KIND_COLOR = { Zainteresowany: '#10B981', Pytanie: '#3B82F6', Krytyczny: '#EF4444', Spam: '#6B7280' };
const PRIORITY_COLOR = { Pilne: '#EF4444', Normalne: '#F59E0B', Niskie: '#6B7280' };

const EXAMPLES = [
  'Cześć! Ile to kosztuje i jakie są terminy?',
  'U koleżanki wyszło słabo, boję się, że u mnie będzie tak samo…',
  'Dzień dobry, czy robicie wysyłkę za granicę? Potrzebuję na prezent do soboty.',
];

export default function ScoutOdpowiedzi({ client }) {
  const [author, setAuthor] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState([]);
  const [err, setErr] = useState('');

  const analyze = async () => {
    if (!message.trim() || loading) return;
    setLoading(true); setErr('');
    try {
      const res = await fetch(`${API}/scout/reply-draft`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          client_name: client.name, industry: client.industry, tone: client.tone,
          offer: client.offer, usp: client.usp || [], avoid: client.avoid || '', author, message,
        }),
      });
      const data = await res.json();
      if (data?.ok && data.result) {
        setResults((r) => [{ ...data.result, author: author || 'Nieznany', message }, ...r]);
        setMessage(''); setAuthor('');
      } else {
        setErr(data?.message || 'Nie udało się przygotować odpowiedzi.');
      }
    } catch {
      setErr('Błąd połączenia. Spróbuj ponownie.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="view-odpowiedzi">
      <style>{`
        @keyframes scoutFadeUp { from { opacity: 0; transform: translateY(14px); } to { opacity: 1; transform: translateY(0); } }
        .scout-reveal { animation: scoutFadeUp .55s cubic-bezier(.22,.9,.36,1) both; }
      `}</style>

      <div className="mb-4">
        <h2 className="text-lg font-bold flex items-center gap-2"><MessageSquare className="w-5 h-5 text-[#FF8A3D]" /> Odpowiedzi</h2>
        <p className="text-xs text-[var(--sc-t45)] mt-0.5 ml-7">Wklej wiadomość lub komentarz — Scout oceni i przygotuje odpowiedź w tonie: {client.tone}.</p>
      </div>

      {/* INPUT */}
      <div className={`${panel} p-4 space-y-3`}>
        <div className="flex flex-col sm:flex-row gap-3">
          <input value={author} onChange={(e) => setAuthor(e.target.value)} placeholder="Autor (opcjonalnie, np. Marta K.)" data-testid="reply-author-input"
            className="sm:w-56 rounded-xl bg-[var(--sc-input)] border border-[var(--sc-border2)] px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400/50" />
          <textarea value={message} onChange={(e) => setMessage(e.target.value)} rows={2} data-testid="reply-input"
            placeholder="Wklej treść wiadomości / komentarza…"
            className="flex-1 rounded-xl bg-[var(--sc-input)] border border-[var(--sc-border2)] px-4 py-3 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-orange-400/50" />
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button onClick={analyze} disabled={loading || !message.trim()} data-testid="reply-analyze-btn"
            className="inline-flex items-center gap-2 rounded-xl bg-[#F26A21] hover:bg-[#FF7C33] px-5 py-2.5 text-sm font-bold transition disabled:opacity-50">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
            {loading ? 'Przygotowuję…' : 'Przygotuj odpowiedź'}
          </button>
          <span className="text-[11px] text-[var(--sc-t30)] mr-1">Wypróbuj:</span>
          {EXAMPLES.map((ex, i) => (
            <button key={i} onClick={() => setMessage(ex)} data-testid={`reply-example-${i}`}
              className="text-[11px] rounded-full border border-[var(--sc-border)] text-[var(--sc-t45)] hover:text-[var(--sc-t80)] hover:border-orange-400/40 px-2.5 py-1 transition max-w-[220px] truncate">
              „{ex}"
            </button>
          ))}
        </div>
      </div>
      {err && <p className="text-sm text-red-400" data-testid="reply-error">{err}</p>}

      {/* RESULTS */}
      {results.length === 0 && !loading && (
        <div className={`${panel} p-8 text-center`}>
          <MessageSquare className="w-10 h-10 mx-auto text-[var(--sc-t15)] mb-3" />
          <p className="text-sm text-[var(--sc-t50)]">Tu pojawią się przygotowane odpowiedzi — z oceną typu wiadomości,
            priorytetem i oznaczeniem leadów.</p>
        </div>
      )}

      <div className="space-y-3">
        {results.map((r, i) => <ReplyCard key={results.length - i} r={r} idx={i} />)}
      </div>
    </div>
  );
}

function ReplyCard({ r, idx }) {
  const [copied, setCopied] = useState(false);
  const kindColor = KIND_COLOR[r.kind] || '#3B82F6';
  const prioColor = PRIORITY_COLOR[r.priority] || '#F59E0B';
  const spam = r.kind === 'Spam';
  const copy = async () => {
    try { await navigator.clipboard.writeText(r.reply); setCopied(true); setTimeout(() => setCopied(false), 2000); } catch { /* noop */ }
  };
  return (
    <div className={`${panel} p-4 scout-reveal`} data-testid={`reply-result-${idx}`}>
      <div className="flex flex-wrap items-center gap-2 mb-2.5">
        <div className="w-8 h-8 rounded-full bg-[var(--sc-surface4)] flex items-center justify-center text-xs font-bold">
          {r.author?.[0]?.toUpperCase() || <User className="w-4 h-4" />}
        </div>
        <span className="font-semibold text-sm mr-1">{r.author}</span>
        <span className={chip} style={{ background: `${kindColor}22`, color: kindColor }}>{r.kind}</span>
        <span className={chip} style={{ background: `${prioColor}22`, color: prioColor }}><Flame className="w-3 h-3" /> {r.priority}</span>
        {r.is_lead && <span className={chip} style={{ background: '#EAB30822', color: '#FACC15' }} data-testid={`reply-lead-badge-${idx}`}><Zap className="w-3 h-3" /> Lead</span>}
      </div>
      <p className="text-sm text-[var(--sc-t60)] mb-3">„{r.message}"</p>
      {!spam && r.reply && (
        <div className="rounded-xl bg-[var(--sc-side)] border border-[var(--sc-border)] p-3.5">
          <div className="text-[10px] uppercase tracking-wide text-orange-300/80 mb-1">Sugerowana odpowiedź</div>
          <p className="text-sm text-[var(--sc-t90)] leading-relaxed">{r.reply}</p>
        </div>
      )}
      {spam && <div className="rounded-xl bg-[var(--sc-surface2)] border border-[var(--sc-border)] p-3 text-sm text-[var(--sc-t45)]">Sugestia: ukryj lub zignoruj — to wygląda na spam.</div>}
      <div className="mt-3 flex flex-wrap items-center gap-3">
        {!spam && r.reply && (
          <button onClick={copy} data-testid={`copy-reply-${idx}`}
            className="inline-flex items-center gap-1.5 rounded-lg bg-[#F26A21] hover:bg-[#FF7C33] px-3.5 py-2 text-sm font-semibold transition">
            {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />} {copied ? 'Skopiowano!' : 'Kopiuj odpowiedź'}
          </button>
        )}
        {r.tip && <span className="text-xs text-[var(--sc-t40)] flex items-center gap-1.5"><Sparkles className="w-3.5 h-3.5 text-orange-300/70" /> {r.tip}</span>}
      </div>
    </div>
  );
}
